import { Storey } from '@shared/models/design/storey.model';
import { IElementProperty } from '@shared/models/element-property.model';
import { OneOfElements } from '@shared/models/project.model';

/**
 * Message when a select count is not one of that property's options.
 */
export const SELECT_COUNT_NOT_IN_OPTIONS_MESSAGE =
  'Property count is not part of options list' as const;

/**
 * Structured validation errors used across validators. Keep strings for backward compatibility.
 */
export class GenericValidationError extends Error {
  readonly type = 'generic' as const;
  /** Back-compat: mirror message on error field for existing call sites */
  readonly error: string;
  constructor(message: string, options?: ValidationErrorOptions) {
    super(message);
    this.name = 'GenericValidationError';
    this.error = this.message;
    this.stack = options?.stack ?? captureOriginStack(2);
  }
}

export class ElementValidationError extends Error {
  readonly type = 'element' as const;
  readonly error: string;
  readonly element?: OneOfElements;
  constructor(message: string, options?: ElementValidationErrorOptions) {
    super(message);
    this.name = 'ElementValidationError';
    this.error = this.message;
    this.element = options?.element;
    this.stack = options?.stack ?? captureOriginStack(2);
  }
}

export class PropertyValidationError extends Error {
  readonly type = 'property' as const;
  readonly error: string;
  readonly property?: IElementProperty;
  readonly element?: OneOfElements;
  constructor(message: string, options?: PropertyValidationErrorOptions) {
    super(message);
    this.name = 'PropertyValidationError';
    this.error = this.message;
    this.element = options?.element;
    this.property = options?.property;
    this.stack = options?.stack ?? captureOriginStack(2);
  }
}

/**
 * Validation error for a storey
 */
export class StoreyValidationError extends Error {
  readonly type = 'storey' as const;
  readonly error: string;
  readonly storey: Storey;
  constructor(message: string, options: StoreyValidationErrorOptions) {
    super(message);
    this.name = 'StoreyValidationError';
    this.error = this.message;
    this.storey = options.storey;
    this.stack = options?.stack ?? captureOriginStack(2);
  }
}

export type ValidationError =
  | GenericValidationError
  | ElementValidationError
  | PropertyValidationError
  | StoreyValidationError;

type ElementValidationErrorOptions = ValidationErrorOptions & {
  element?: OneOfElements;
};
type PropertyValidationErrorOptions = ElementValidationErrorOptions & {
  property?: IElementProperty;
};

type StoreyValidationErrorOptions = ValidationErrorOptions & {
  storey: Storey;
};

export type ValidationErrorOptions = {
  /** Manually set origin stack (overrides auto-captured stack) */
  stack?: string;
};

/**
 * A validator returns true when valid, or a structured error when invalid.
 */
export type ValidationResult = true | ValidationError;

/**
 * A validator returns the item if valid, or a structured error when invalid.
 */
export type ValidatedValue<T> = T | ValidationError;

export type ValidationFn = () => ValidationResult;

/**
 * Capture a stack trace and drop a number of leading frames so the top frame
 * appears to come from the caller rather than the helper itself.
 */
const captureOriginStack = (framesToDrop = 1): string | undefined => {
  const origin = new Error();
  if (!origin.stack) return undefined;
  const lines = origin.stack.split('\n');
  const startIndex = Math.min(1 + framesToDrop, lines.length);
  return [lines[0], ...lines.slice(startIndex)].join('\n');
};
